import { useBusinessUserTokenRequest, useMeQuery } from '../api/api.portal';
import { LINKS } from '../helpers/consts';
import { StackParamList } from '../navigation/navigation.types';
import { useSelectedBusinessUserStore } from '../screens/home/selectedBusinessUser.store';
import socket from '../socket/socket';
import { useRoute } from '@react-navigation/native';
import throttle from 'lodash/throttle';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import api from '../api/api';
import { ADMIN_TOKEN_USER_ID, ADMIN_TOKEN_USER_LOGIN, API_HOST_URL } from '../api/api.const';
import { AuthContext } from '../screens/auth/auth.utils';
import { isWebMobile } from './device';

export interface UseFetchOpts {
  skip?: boolean;
  publicEndpoint?: boolean;
}

export const useFetch = <T>(url: string, opts: UseFetchOpts = {}) => {
  const { skip, publicEndpoint } = opts || {};
  const { webAuth } = useContext(AuthContext);

  const [status, setStatus] = useState<'loading' | 'failed' | 'completed' | 'skipped'>(
    skip ? 'skipped' : 'loading',
  );
  const [data, setData] = useState<T>();

  useEffect(() => {
    if ((!webAuth && !publicEndpoint) || skip) {
      return;
    }

    const fetchData = async () => {
      try {
        const [response] = await api(
          `${API_HOST_URL}/${url}`,
          { method: 'GET' },
          { ...(webAuth?.accessToken && { Authorization: `Bearer ${webAuth.accessToken}` }) },
        );

        setData(response.data as T);
        setStatus('completed');
      } catch (e) {
        console.error(e);
        setStatus('failed');
      }
    };

    fetchData();
  }, [url, setStatus, webAuth, skip, publicEndpoint]);

  const refetch = useCallback(async () => {
    if ((!webAuth && !publicEndpoint) || skip) {
      return;
    }

    const [response] = await api(
      `${API_HOST_URL}/${url}`,
      { method: 'GET' },
      { ...(webAuth?.accessToken && { Authorization: `Bearer ${webAuth.accessToken}` }) },
    );

    setData(response.data as T);
  }, [webAuth, publicEndpoint, skip, url]);

  if (!skip && status === 'skipped') {
    refetch();
  }

  return {
    loading: status === 'loading',
    data: status === 'completed' ? data : undefined,
    skipped: status === 'skipped',
    refetch,
  };
};

export const useIsWebMobile = () => {
  const dimensions = useWindowDimensions();

  return Platform.OS === 'web' && dimensions.width < 768;
};

export const useIsMobile = () => {
  const dimensions = useWindowDimensions();

  if (Platform.OS === 'web') {
    return (
      isWebMobile(navigator.userAgent || navigator.vendor || (window as any).opera) ||
      dimensions.width < 768
    );
  }

  return true;
};

export const useThrottle = <T extends (..._args: any) => any>(func: T, time = 100, deps = []) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(throttle(func, time, { leading: true, trailing: true }), deps);
};

type SocketConnectionSwitcherProps = { redirectTo?: keyof StackParamList; active?: boolean };

export const useSocketConnectionSwitcher = (options?: SocketConnectionSwitcherProps) => {
  const { active, redirectTo } = options || { active: true };

  const route = useRoute();
  const { data: profile } = useMeQuery();
  const { webAuth } = useContext(AuthContext);
  const [request] = useBusinessUserTokenRequest({
    adminRequest: webAuth?.admin,
    tokenType: 'server',
  });
  const { selectedUserId, selectedUserLogin } = useSelectedBusinessUserStore();

  const redirect = redirectTo || route.name;

  useEffect(() => {
    if (!active || (!webAuth?.admin && (!selectedUserId || !profile?.userId))) {
      return;
    }

    if (selectedUserId === webAuth?.userId) {
      socket.connectWithToken('');
    } else if (socket.currentlyConnectedUserId !== selectedUserId) {
      request(selectedUserId || ADMIN_TOKEN_USER_ID, selectedUserLogin || ADMIN_TOKEN_USER_LOGIN)
        .then(data => data.data.access_token)
        .then(token =>
          socket.connectWithToken(token, selectedUserId || ADMIN_TOKEN_USER_ID, redirect),
        );
    }
  }, [selectedUserId, profile?.userId, active]);
};

export const useAppConfig = () => {
  const config = {
    appName: 'Streamster',
    isMobileAppAvailable: false,
    isWhiteLabelMode: false,
    links: LINKS,
  };

  const override = (window as any)?.stcfg;

  if (override) {
    Object.assign(config, override);
  }

  return config;
};

export const isWhiteLabelMode = () => useAppConfig().isWhiteLabelMode === true;

export const getAppName = () => useAppConfig().appName || 'Streamster';
